import type { FormikProps } from 'formik';
import type { ComponentProps } from 'react';
import React from 'react';
import InputField from '@savvy-kit/patterns/input-field';
import { formatManualDollarAmountEntry } from 'src/utils/formatting';

type MonetaryInputTextFieldProps<T extends object> = {
  readonly formik?: FormikProps<T>;
  // Value filtered to be stored in DB
  readonly onChangeSave?: (filteredValue: string) => void;
  readonly id?: string;
  readonly value?: string | number | null;
} & Omit<ComponentProps<typeof InputField>, 'id' | 'value'>;

const strip = (value: string) => value.replace(/[^\d.]/g, '');

function filterInput(oldText: string, oldCursor: number) {
  const newText = formatManualDollarAmountEntry(strip(oldText));
  let newCursor = oldCursor;
  if (newText.length === oldText.length - 1) {
    newCursor = oldCursor - 1;
  } else if (newText.length === oldText.length - 2) {
    newCursor = oldCursor - 2;
  } else if (newText.length === oldText.length + 1) {
    newCursor = oldCursor + 1;
  } else if (newText.length === oldText.length + 2) {
    newCursor = oldCursor + 2;
  }
  return [newText, Math.max(newCursor, 0)];
}

type Fxn = () => void;
function useRunAfterUpdate() {
  const afterPaintRef = React.useRef<Fxn | null>(null);
  React.useLayoutEffect(() => {
    if (afterPaintRef.current) {
      afterPaintRef.current();
      afterPaintRef.current = null;
    }
  });
  const runAfterUpdate = (fn: Fxn) => (afterPaintRef.current = fn);
  return runAfterUpdate;
}

export default function MonetaryInputTextField<T extends object>(props: MonetaryInputTextFieldProps<T>) {
  const { formik, onChangeSave, id, containerClassName, prependInputAddon, ...inputFieldProps } = props;
  const runAfterUpdate = useRunAfterUpdate();

  return (
    <InputField
      id={id ?? inputFieldProps.name ?? 'monetary-input'}
      // Defaults, deliberately above the spread so a caller can replace either —
      // the container margin stands in for the old `margin="normal"`.
      containerClassName={containerClassName ?? 'mt-4 mb-2'}
      prependInputAddon={prependInputAddon ?? '$'}
      {...inputFieldProps}
      value={props.value ? formatManualDollarAmountEntry(String(props.value)) : ''}
      onBlur={(e) => {
        formik?.handleBlur(e);
      }}
      onChange={(e) => {
        const input = e.target;
        const cursor = input.selectionStart;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
        const [filteredValue, newCursor] = filterInput(e.target.value, cursor!);
        const fieldValue = String(filteredValue).replace(/,/g, '');
        const fieldName = inputFieldProps.name;

        if (fieldName === undefined) throw new Error('TextField name is undefined');

        if (formik) {
          formik.setFieldValue(fieldName, fieldValue);
          formik.setFieldTouched(fieldName, true);
        }
        const valueToSave =
          // Trailing decimal
          fieldValue.indexOf('.') === fieldValue.length - 1 ? fieldValue.slice(0, -1) : fieldValue;
        runAfterUpdate(() => {
          input.selectionStart = Number(newCursor);
          input.selectionEnd = Number(newCursor);
        });
        if (onChangeSave) onChangeSave(valueToSave);
      }}
      type="text"
    />
  );
}
